<template>
    <a
        v-if="href"
        :href="href"
        :target="target"
        :class="buttonClasses"
        role="button"
        v-bind="$attrs"
        @click="handleClick"
    >
        <slot></slot>
    </a>
    <button
        v-else
        v-bind="$attrs"
        :class="buttonClasses"
        :disabled="disabled"
        @click="handleClick"
    >
        <slot></slot>
    </button>
</template>

<script>
const buttonVariants = Object.freeze({
    primary: 'primary',
    secondary: 'secondary',
    accent: 'accent',
    danger: 'danger',
    tertiary: 'tertiary',
    ghost: 'ghost',
    subtle: 'subtle',
});

export default {
    name: 'DkButton',
    props: {
        variant: {
            type: String,
            default: 'primary',
            validator(value) {
                return Object.keys(buttonVariants).includes(value);
            },
        },
        href: {
            type: String,
            default: null,
        },
        target: {
            type: String,
            default: null,
        },
        isIconOnly: {
            type: Boolean,
            default: false,
        },
        isSmall: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        isInverse: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        buttonClasses() {
            let classes = [
                'dk-button',
                'no-external-style',
                'tw-outline-none',
                'tw-cursor-pointer',
                'tw-inline-flex',
                'tw-justify-center',
                'tw-items-center',
                // ...this.class,
            ];

            if (this.variant === buttonVariants.ghost || this.variant === buttonVariants.subtle) {
                classes.push(
                    'tw-text-black focus:tw-ring-1 focus:tw-ring-offset-1 focus:tw-ring-black tw-bg-transparent tw-border-none'
                );

                if (this.isIconOnly) {
                    classes.push('tw-w-8 tw-h-8');
                } else {
                    classes.push('tw-px-1');
                }

                if (this.isSmall) {
                    classes.push('tw-text-sm');
                }

                if (this.disabled) {
                    classes.push('tw-text-slate-300 tw-pointer-events-none');
                }

                if (this.variant === buttonVariants.ghost) {
                    classes.push('tw-font-bold hover:lg:tw-text-brand-primary');
                }

                if (this.variant === buttonVariants.subtle) {
                    classes.push('tw-underline hover:tw-font-bold hover:tw-text-slate-700');
                }

                return classes;
            }

            classes.push('tw-font-bold hover:tw-text-white tw-border-solid');

            if (this.isIconOnly) {
                if (this.isSmall) {
                    classes.push('tw-w-8', 'tw-h-8');
                } else {
                    classes.push('tw-w-12', 'tw-h-12');
                }
            } else {
                classes.push('tw-px-6');

                if (this.isSmall) {
                    classes.push('tw-h-8 tw-text-sm');
                } else {
                    classes.push('tw-h-8 lg:tw-h-12 lg:tw-text-lg');
                }
            }

            if (this.variant !== buttonVariants.tertiary) {
                classes.push('tw-text-white');

                if (this.disabled) {
                    classes.push('tw-bg-slate-200', 'tw-pointer-events-none');
                }
            } else if (this.disabled) {
                // tertiary disabled
                classes.push('tw-border', 'tw-border-slate-300', 'tw-text-slate-300', 'tw-pointer-events-none');
            }

            if (this.disabled) {
                return classes;
            }

            switch (this.variant) {
                case buttonVariants.primary:
                    classes.push(
                        'tw-bg-brand-primary',
                        'hover:tw-bg-blue-500',
                        'focus:tw-ring-1',
                        'focus:tw-ring-offset-1',
                        'focus:tw-ring-brand-primary'
                    );

                    break;
                case buttonVariants.secondary:
                    classes.push(
                        'tw-bg-brand-secondary',
                        'hover:tw-bg-black',
                        'focus:tw-ring-1',
                        'focus:tw-ring-offset-1',
                        'focus:tw-ring-brand-secondary'
                    );
                    break;
                case buttonVariants.accent:
                    classes.push(
                        'tw-bg-brand-accent',
                        'hover:tw-bg-green-600',
                        'focus:tw-ring-1',
                        'focus:tw-ring-offset-1',
                        'focus:tw-ring-brand-accent'
                    );
                    break;
                case buttonVariants.danger:
                    classes.push(
                        'tw-bg-feedback-danger',
                        'hover:tw-bg-red-400',
                        'focus:tw-ring-1',
                        'focus:tw-ring-offset-1',
                        'focus:tw-ring-feedback-danger'
                    );
                    break;
                case buttonVariants.tertiary:
                    if (this.isInverse) {
                        classes.push(
                            'hover:tw-bg-black',
                            'tw-border',
                            'tw-border-white',
                            'focus:tw-ring-1',
                            'focus:tw-ring-offset-1',
                            'focus:tw-ring-white',
                            'tw-text-white'
                        );
                    } else {
                        classes.push(
                            'hover:tw-bg-black',
                            'tw-border',
                            'tw-border-black',
                            'focus:tw-ring-1',
                            'focus:tw-ring-offset-1',
                            'focus:tw-ring-black'
                        );
                    }

                    break;
                case buttonVariants.subtle:
                    classes = [
                        'tw-font-normal',
                        'tw-leading-sm',
                        'tw-underline',
                        'tw-text-negative',
                        'tw-text-base',
                        'tw-cursor-pointer',
                        'hover:tw-font-bold',
                    ];
                    break;
            }

            return classes;
        },
    },
    methods: {
        handleClick(e) {
            this.$emit('click', e);
        },
    },
};
</script>

<style></style>
